import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { flyProductToCart } from '@theme/fly-to-cart';

export function getGiftConfig() {
  const cfg = (typeof window !== 'undefined' && window.SkinrooGiftConfig) || {};
  return {
    enabled: cfg.enabled !== false,
    threshold: typeof cfg.threshold === 'number' ? cfg.threshold : 250000, // 2500 INR in cents/paise
    variantId: Number(cfg.variantId) || 50838155231490, // Witch's Pouch Stick Shadow - Pink
  };
}

export const QUALIFYING_VARIANTS = [];
export const QUALIFYING_PRODUCTS = [];
export const GIFT_VARIANT_ID = 50838155231490;

/**
 * Skinroo Cart Manager
 * Unified AJAX Cart Controller with Fly-To-Cart Animation & Instant Drawer/Counter Updates
 */
export class SkinrooCartManager {
  _isSyncingGift = false;
  _giftSyncInitialized = false;
  /**
   * Add a single product variant to cart
   * @param {Object} params
   * @param {number|string} params.id - Variant ID
   * @param {number} [params.quantity=1] - Quantity
   * @param {Element} [params.sourceElement] - Element to fly from (button or card)
   * @param {string} [params.imageSrc] - Image URL to fly
   * @param {Function} [params.onStart] - Callback on start
   * @param {Function} [params.onSuccess] - Callback on success
   * @param {Function} [params.onError] - Callback on error
   */
  async addToCart({ id, quantity = 1, sourceElement = null, imageSrc = '', onStart, onSuccess, onError }) {
    if (!id) return;
    return this.addItemsToCart({
      items: [{ id: Number(id), quantity: Number(quantity) || 1 }],
      sourceElement,
      imageSrc,
      onStart,
      onSuccess,
      onError,
    });
  }

  /**
   * Add multiple items to cart (bundles / routines)
   * @param {Object} params
   * @param {Array<{id: number|string, quantity: number}>} params.items
   * @param {Element} [params.sourceElement]
   * @param {string} [params.imageSrc]
   * @param {Function} [params.onStart]
   * @param {Function} [params.onSuccess]
   * @param {Function} [params.onError]
   */
  async addItemsToCart({ items, sourceElement = null, imageSrc = '', onStart, onSuccess, onError }) {
    if (!items || items.length === 0) return;

    if (typeof onStart === 'function') onStart();

    // 1. Play Fly-to-Cart Animation immediately
    try {
      const destCartIcon = document.querySelector('.header-actions__cart-icon') || document.querySelector('cart-icon');
      let flySource = sourceElement;
      let flyImg = imageSrc;

      if (!flyImg && sourceElement) {
        const card = sourceElement.closest('.skinroo-card, .skinroo-prod-card, .skinroo-gift-card, .skinroo-stl-card, .collection-item, li, product-card') || sourceElement;
        const img = card.querySelector('img');
        if (img) {
          flyImg = img.currentSrc || img.src;
          if (!flySource || flySource.tagName === 'BUTTON') {
            flySource = img;
          }
        }
      }

      if (flySource && destCartIcon) {
        flyProductToCart(flySource, flyImg, destCartIcon);
      }
    } catch (animErr) {
      console.warn('[SkinrooCart] Animation warning:', animErr);
    }

    // 2. Prepare payload & CartLinesUpdateEvent promise
    const rootRoute = (typeof window !== 'undefined' && window.Shopify?.routes?.root) || '/';
    const deferredPromise = CartLinesUpdateEvent.createPromise();

    const lineItems = items.map((it) => ({
      merchandiseId: String(it.id),
      quantity: Number(it.quantity) || 1,
    }));

    // Identify sections needed by the cart drawer
    const cartDrawerSectionEl = document.querySelector('cart-drawer-component, [data-section-id]');
    const sectionId = cartDrawerSectionEl?.dataset?.sectionId || 'cart-drawer-section';

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lineItems,
        promise: deferredPromise.promise,
      })
    );

    try {
      const response = await fetch(`${rootRoute}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: items.map((it) => ({ id: Number(it.id), quantity: Number(it.quantity) || 1 })),
          sections: [sectionId, 'cart-drawer-section', 'cart-drawer', 'cart-icon-bubble'].join(','),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || errorData.message || 'Cart add failed');
      }

      const responseData = await response.json();

      // 3. Fetch latest cart status to update total counts
      const cartRes = await fetch(`${rootRoute}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      const ajaxCart = await cartRes.json();

      // 4. Resolve the standard Shopify event promise to trigger cart-drawer morph & auto-open
      deferredPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          itemCount: ajaxCart.item_count,
          sections: responseData.sections,
          didError: false,
        },
      });

      // 5. Update header cart bubble counter immediately
      this.updateHeaderCartBubble(ajaxCart.item_count);

      // 6. Open Cart Drawer if present
      this.openCartDrawer();

      if (typeof onSuccess === 'function') {
        onSuccess(responseData, ajaxCart);
      }

      this.syncFreeGift(ajaxCart, 'add');

      return responseData;
    } catch (err) {
      console.error('[SkinrooCart] Add to cart error:', err);
      deferredPromise.reject(err);

      if (typeof onError === 'function') {
        onError(err);
      } else {
        alert(err.message || 'Failed to add item to cart');
      }
    }
  }

  /**
   * Update header cart bubble counter
   * @param {number} count
   */
  updateHeaderCartBubble(count) {
    const cartIconEls = document.querySelectorAll('cart-icon, .header-actions__cart-icon');
    cartIconEls.forEach((cartIconEl) => {
      if (typeof cartIconEl.renderCartBubble === 'function') {
        cartIconEl.renderCartBubble(count, true);
      } else {
        const bubbleCount = cartIconEl.querySelector('[ref="cartBubbleCount"], .cart-bubble__text-count, .cart-bubble');
        if (bubbleCount) {
          bubbleCount.textContent = count < 100 ? String(count) : '';
          bubbleCount.classList.remove('hidden', 'visually-hidden');
        }
        cartIconEl.classList.toggle('header-actions__cart-icon--has-cart', count > 0);
      }
    });

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(count),
        timestamp: Date.now(),
      })
    );
  }

  /**
   * Open the theme's cart drawer
   */
  openCartDrawer() {
    setTimeout(() => {
      const themeDrawer = document.querySelector('theme-drawer#cart-drawer, theme-drawer[id="cart-drawer"]');
      if (themeDrawer && typeof themeDrawer.open === 'function' && !themeDrawer.isOpen) {
        themeDrawer.open();
        return;
      }

      const cartDrawerComponent = document.querySelector('cart-drawer-component');
      const drawerParent = cartDrawerComponent?.closest('theme-drawer');
      if (drawerParent && typeof drawerParent.open === 'function' && !drawerParent.isOpen) {
        drawerParent.open();
      }
    }, 150);
  }

  /**
   * Auto-bind all custom Skinroo add-to-cart buttons across the page
   */
  bindAllForms() {
    // 1. Custom main collection cards (.sr-atc-button)
    document.querySelectorAll('.sr-atc-button:not([data-skinroo-bound])').forEach((btn) => {
      btn.dataset.skinrooBound = 'true';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const form = btn.closest('form');
        const card = btn.closest('.sr-product-card');
        const variantId = form?.querySelector('input[name="id"]')?.value;
        const img = card?.querySelector('.sr-image-wrapper img');

        if (!variantId) return;

        const textSpan = btn.querySelector('.sr-atc-text');
        const origText = textSpan ? textSpan.textContent : 'ADD TO CART';
        btn.disabled = true;
        if (textSpan) textSpan.textContent = 'ADDING...';

        this.addToCart({
          id: variantId,
          quantity: 1,
          sourceElement: img || btn,
          imageSrc: img?.currentSrc || img?.src || '',
          onSuccess: () => {
            if (textSpan) textSpan.textContent = 'ADDED ✓';
            setTimeout(() => {
              if (textSpan) textSpan.textContent = origText;
              btn.disabled = false;
            }, 1800);
          },
          onError: () => {
            if (textSpan) textSpan.textContent = origText;
            btn.disabled = false;
          },
        });
      });
    });

    // 2. Gift hampers (.skinroo-gift-btn)
    document.querySelectorAll('.skinroo-gift-btn:not(.disabled):not([data-skinroo-bound])').forEach((btn) => {
      btn.dataset.skinrooBound = 'true';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const form = btn.closest('form');
        const card = btn.closest('.skinroo-gift-card');
        const variantId = form?.querySelector('input[name="id"]')?.value;
        const img = card?.querySelector('.skinroo-gift-image-box img');

        if (!variantId) return;

        const origText = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding...';

        this.addToCart({
          id: variantId,
          quantity: 1,
          sourceElement: img || btn,
          imageSrc: img?.currentSrc || img?.src || '',
          onSuccess: () => {
            btn.textContent = 'Added ✓';
            setTimeout(() => {
              btn.textContent = origText;
              btn.disabled = false;
            }, 1800);
          },
          onError: () => {
            btn.textContent = origText;
            btn.disabled = false;
          },
        });
      });
    });

    // 3. Shop The Look (.skinroo-stl-btn)
    document.querySelectorAll('.skinroo-stl-btn:not(.disabled):not([data-skinroo-bound])').forEach((btn) => {
      btn.dataset.skinrooBound = 'true';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const form = btn.closest('form');
        const card = btn.closest('.skinroo-stl-card');
        const variantId = form?.querySelector('input[name="id"]')?.value;
        const img = card?.querySelector('.skinroo-stl-card-image img');

        if (!variantId) return;

        const origText = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding...';

        this.addToCart({
          id: variantId,
          quantity: 1,
          sourceElement: img || btn,
          imageSrc: img?.currentSrc || img?.src || '',
          onSuccess: () => {
            btn.textContent = 'Added ✓';
            setTimeout(() => {
              btn.textContent = origText;
              btn.disabled = false;
            }, 1800);
          },
          onError: () => {
            btn.textContent = origText;
            btn.disabled = false;
          },
        });
      });
    });

    // 4. Collection Links (.add-to-cart-btn)
    document.querySelectorAll('.product-card .add-to-cart-btn:not([data-skinroo-bound])').forEach((btn) => {
      btn.dataset.skinrooBound = 'true';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const form = btn.closest('form');
        const card = btn.closest('.product-card');
        const variantId = form?.querySelector('input[name="id"]')?.value;
        const img = card?.querySelector('.image-container img, img');

        if (!variantId) return;

        const origText = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding...';

        this.addToCart({
          id: variantId,
          quantity: 1,
          sourceElement: img || btn,
          imageSrc: img?.currentSrc || img?.src || '',
          onSuccess: () => {
            btn.textContent = 'Added ✓';
            setTimeout(() => {
              btn.textContent = origText;
              btn.disabled = false;
            }, 1800);
          },
          onError: () => {
            btn.textContent = origText;
            btn.disabled = false;
          },
        });
      });
    });
  }

  /**
   * Initialize free gift synchronization listeners
   */
  initFreeGiftSync() {
    if (this._giftSyncInitialized) return;
    this._giftSyncInitialized = true;

    // Track when user manually removes the free gift item
    document.addEventListener(
      'click',
      (e) => {
        const removeBtn = e.target.closest(
          '.cart-items__remove, [on\\:click*="/onLineItemRemove"], button[aria-label*="Remove"]'
        );
        if (removeBtn) {
          const row = removeBtn.closest('tr[data-variant-id]');
          const { variantId } = getGiftConfig();
          if (row && Number(row.dataset.variantId) === variantId) {
            sessionStorage.setItem('sr_gift_manually_removed', 'true');
          }
        }
      },
      true
    );

    // Listen to standard cart updates across all theme components (forms, quick-add, drawer changes)
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      if (event.detail?._isGiftSync) return;
      const action = event.action;
      event.promise
        ?.then((result) => {
          const cart = result?.cart;
          this.syncFreeGift(cart, action);
        })
        .catch(() => {});
    });

    // Check cart on initial load
    this.checkCartOnLoad();
  }

  async checkCartOnLoad() {
    try {
      const rootRoute = (typeof window !== 'undefined' && window.Shopify?.routes?.root) || '/';
      const res = await fetch(`${rootRoute}cart.js`, { headers: { Accept: 'application/json' } });
      if (res.ok) {
        const cart = await res.json();
        this.syncFreeGift(cart, 'check');
      }
    } catch (e) {
      // Silent error handling for initial load
    }
  }

  /**
   * Automatically synchronizes the free gift product
   * @param {Object} [cartData]
   * @param {string} [triggerAction] - 'add' | 'change' | 'check'
   */
  async syncFreeGift(cartData = null, triggerAction = '') {
    if (this._isSyncingGift) return;

    try {
      const { enabled, threshold, variantId } = getGiftConfig();
      if (!enabled || !variantId) return;

      const rootRoute = (typeof window !== 'undefined' && window.Shopify?.routes?.root) || '/';
      let cart = cartData;
      if (!cart || !Array.isArray(cart.items)) {
        const res = await fetch(`${rootRoute}cart.js`, { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        cart = await res.json();
      }

      // Calculate net qualifying spend across all items EXCEPT the free gift itself
      let qualifyingSpend = 0;
      let giftItem = null;

      for (const item of cart.items) {
        const vid = Number(item.variant_id || item.id);
        if (vid === variantId) {
          giftItem = item;
        } else {
          // Use final_line_price to accurately factor in item discounts/BOGO
          qualifyingSpend += Number(item.final_line_price ?? item.line_price ?? 0);
        }
      }

      const qualifies = qualifyingSpend >= threshold;

      // If user added a new item and cart now qualifies, reset the manual removal flag
      if (triggerAction === 'add' && qualifies) {
        sessionStorage.removeItem('sr_gift_manually_removed');
      }

      const isManuallyRemoved = sessionStorage.getItem('sr_gift_manually_removed') === 'true';

      const cartDrawerSectionEl = document.querySelector('cart-drawer-component, [data-section-id]');
      const sectionId = cartDrawerSectionEl?.dataset?.sectionId || 'cart-drawer-section';
      const sectionsToUpdate = [sectionId, 'cart-drawer-section', 'cart-drawer', 'cart-icon-bubble'].join(',');

      // Condition 1: Qualifies, gift NOT in cart, and NOT manually removed
      if (qualifies && !giftItem && !isManuallyRemoved) {
        this._isSyncingGift = true;

        const addRes = await fetch(`${rootRoute}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: sectionsToUpdate,
          }),
        });

        if (addRes.ok) {
          const responseData = await addRes.json();
          const updatedCartRes = await fetch(`${rootRoute}cart.js`, { headers: { Accept: 'application/json' } });
          const updatedCart = await updatedCartRes.json();

          this.updateHeaderCartBubble(updatedCart.item_count);

          const deferredPromise = CartLinesUpdateEvent.createPromise();
          document.dispatchEvent(
            new CartLinesUpdateEvent({
              action: 'add',
              context: 'cart',
              lines: [{ merchandiseId: String(variantId), quantity: 1 }],
              promise: deferredPromise.promise,
            })
          );

          deferredPromise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updatedCart),
            detail: {
              items: updatedCart.items,
              itemCount: updatedCart.item_count,
              sections: responseData.sections,
              didError: false,
              _isGiftSync: true,
            },
          });
        }
      }
      // Condition 2: Dropped below threshold, but gift IS in cart -> Auto remove gift!
      else if (!qualifies && giftItem) {
        this._isSyncingGift = true;

        const changeRes = await fetch(`${rootRoute}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: giftItem.key || String(variantId),
            quantity: 0,
            sections: sectionsToUpdate,
          }),
        });

        if (changeRes.ok) {
          const responseData = await changeRes.json();
          const updatedCartRes = await fetch(`${rootRoute}cart.js`, { headers: { Accept: 'application/json' } });
          const updatedCart = await updatedCartRes.json();

          this.updateHeaderCartBubble(updatedCart.item_count);

          const deferredPromise = CartLinesUpdateEvent.createPromise();
          document.dispatchEvent(
            new CartLinesUpdateEvent({
              action: 'remove',
              context: 'cart',
              lines: [{ merchandiseId: String(variantId), quantity: 0 }],
              promise: deferredPromise.promise,
            })
          );

          deferredPromise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updatedCart),
            detail: {
              items: updatedCart.items,
              itemCount: updatedCart.item_count,
              sections: responseData.sections,
              didError: false,
              _isGiftSync: true,
            },
          });
        }
      }
    } catch (err) {
      console.warn('[SkinrooCart] syncFreeGift error:', err);
    } finally {
      this._isSyncingGift = false;
    }
  }
}

export const SkinrooCart = new SkinrooCartManager();

if (typeof window !== 'undefined') {
  window.SkinrooCart = SkinrooCart;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      SkinrooCart.bindAllForms();
      SkinrooCart.initFreeGiftSync();
    });
  } else {
    SkinrooCart.bindAllForms();
    SkinrooCart.initFreeGiftSync();
  }

  // Also bind when dynamic sections re-render or infinite scroll loads
  document.addEventListener('shopify:section:load', () => SkinrooCart.bindAllForms());
  document.addEventListener('infinite-scroll:loaded', () => SkinrooCart.bindAllForms());
}
